import { z } from 'zod';
import { objectIdSchema } from './common.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §6b] A NOTICE — "THIS ORDER WAS TAKEN BACK TO DRAFT BY A SUPER ADMIN".
 *
 *  ⚠️ STORED, AND NOT THE BELL. The bell answers "what work is LEFT" and is derived on every read
 *  ([C1g §1]); a notice records that something was TOLD, which a derived count cannot remember —
 *  [C3c.1 §6d]'s two mechanisms, and the reason they may not share a model. A notice is written once,
 *  by the revert, to every officer named on the order and to the nodal officers of every unit it was
 *  addressed to, and it is cleared by READING (`seenAt`).
 *
 *  ⚠️ IT OUTLIVES THE ORDER. A reverted draft may be deleted outright, so the notice carries its own
 *  snapshot of what the order was called; it never needs the order to render.
 *
 *  ⚠️ IT NEVER NAMES THE SUPER ADMIN. The text says "a super admin"; who it was is on the audit row.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_NOTICE_KINDS = ['REVERTED_TO_DRAFT'] as const;
export type ArrangementNoticeKind = (typeof ARRANGEMENT_NOTICE_KINDS)[number];

/** How many notices one read returns, newest first. `total` and `unseenCount` stay exact. */
export const ARRANGEMENT_NOTICE_LIMIT = 20;

export const arrangementNoticeSchema = z.object({
  id: objectIdSchema,
  kind: z.enum(ARRANGEMENT_NOTICE_KINDS),
  arrangementId: objectIdSchema,
  /** The order as it was when the notice was written. */
  reference: z.string(),
  paNumber: z.string().nullable(),
  title: z.string(),
  /** The super admin's stated reason. */
  reason: z.string(),
  createdAt: z.iso.datetime(),
  /** `null` until the recipient has read it. */
  seenAt: z.iso.datetime().nullable(),
});

/**
 * The dashboard's read (`GET /notifications/notices`). `arrangementVisible` says whether the caller
 * can still open the order — it may have been deleted, or the caller's sight may have moved — so a
 * row never links to a page that answers 404.
 */
export const dashboardArrangementNoticeSchema = arrangementNoticeSchema.extend({
  arrangementVisible: z.boolean(),
});

export const arrangementNoticesSchema = z.object({
  total: z.number().int().min(0),
  unseenCount: z.number().int().min(0),
  items: z.array(dashboardArrangementNoticeSchema),
});
